import { SWIFTFOX_PALETTE } from '@fox/atoms/palette';
import { grey } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import { lightTheme } from '../../themes/light';

export type ChipStatePalette = {
	text: string;
	background: string;
	outline: string;
	hover: string;
	pressed: string;
	focus: string;
};

export type ChipComponentPalette = {
	focusOutline: string;
	data: Record<'default' | 'magenta' | 'purple' | 'teal' | 'green' | 'orange' | 'blue' | 'light', ChipStatePalette>;
	status: Record<'default' | 'error' | 'warning' | 'info' | 'success', ChipStatePalette>;
};

type SemanticPalette = {
	main: string;
	dark: string;
	light: string;
	_states?: {
		focusVisible?: string;
	};
};

export type ChipPaletteSource = {
	mode: 'light' | 'dark';
	text: {
		primary: string;
		secondary: string;
		_states: {
			focus: string;
			focusVisible: string;
		};
	};
	action: {
		hover: string;
		selected: string;
		focus: string;
	};
	error: SemanticPalette;
	warning: SemanticPalette;
	info: SemanticPalette;
	success: SemanticPalette;
};

const decorativeChip = (text: string, background: string, outline: string, darkMode: boolean): ChipStatePalette => ({
	text,
	background: darkMode ? background : alpha(background, 0.5),
	outline,
	hover: darkMode ? outline : alpha(background, 0.8),
	pressed: outline,
	focus: darkMode ? background : alpha(background, 0.5)
});

const semanticChip = (palette: SemanticPalette, darkMode: boolean, darkBackground: string): ChipStatePalette =>
	darkMode
		? {
				text: palette.dark,
				background: darkBackground,
				outline: palette.main,
				hover: palette.light,
				pressed: palette.light,
				focus: darkBackground
			}
		: {
				text: palette.main,
				background: alpha(palette.light, 0.2),
				outline: palette.light,
				hover: alpha(palette.light, 0.5),
				pressed: palette.light,
				focus: palette._states?.focusVisible ?? alpha(palette.main, 0.3)
			};

export const createChipPalette = (palette: ChipPaletteSource): ChipComponentPalette => {
	const darkMode = palette.mode === 'dark';

	return {
		focusOutline: palette.text.primary,
		data: {
			default: {
				text: palette.text.primary,
				background: palette.action.selected,
				outline: darkMode ? grey[700] : grey[400],
				hover: palette.text._states.focus,
				pressed: palette.text._states.focusVisible,
				focus: alpha(palette.text.primary, 0.2)
			},
			magenta: decorativeChip(
				SWIFTFOX_PALETTE.magenta['50-berryWine'],
				SWIFTFOX_PALETTE.magenta['10-lavendar'],
				SWIFTFOX_PALETTE.magenta['20-blushPetal'],
				darkMode
			),
			purple: decorativeChip(
				SWIFTFOX_PALETTE.purple['50-amethyst'],
				SWIFTFOX_PALETTE.purple['10-lilacMist'],
				SWIFTFOX_PALETTE.purple['20-softViolet'],
				darkMode
			),
			teal: decorativeChip(
				SWIFTFOX_PALETTE.teal['50-oceanTeal'],
				SWIFTFOX_PALETTE.teal['10-arcticBreeze'],
				SWIFTFOX_PALETTE.teal['20-coolSky'],
				darkMode
			),
			green: decorativeChip(
				SWIFTFOX_PALETTE.green['50-freshMeadow'],
				SWIFTFOX_PALETTE.green['10-mintFrost'],
				SWIFTFOX_PALETTE.green['20-springMist'],
				darkMode
			),
			orange: decorativeChip(
				SWIFTFOX_PALETTE.orange['50-espresso'],
				SWIFTFOX_PALETTE.orange['10-apricotCream'],
				SWIFTFOX_PALETTE.orange['20-goldenSand'],
				darkMode
			),
			blue: darkMode
				? decorativeChip(
						SWIFTFOX_PALETTE.blue['60-midnight'],
						SWIFTFOX_PALETTE.blue['10-icyMist'],
						SWIFTFOX_PALETTE.blue['20-frostedAzure'],
						true
					)
				: {
						text: SWIFTFOX_PALETTE.blue['50-deepOcean'],
						background: SWIFTFOX_PALETTE.blue['05-glacialAir'],
						outline: SWIFTFOX_PALETTE.blue['20-frostedAzure'],
						hover: SWIFTFOX_PALETTE.blue['10-icyMist'],
						pressed: SWIFTFOX_PALETTE.blue['20-frostedAzure'],
						focus: SWIFTFOX_PALETTE.blue['05-glacialAir']
					},
			light: {
				text: palette.text.primary,
				background: 'transparent',
				outline: darkMode ? grey[700] : grey[400],
				hover: palette.action.hover,
				pressed: palette.action.selected,
				focus: 'transparent'
			}
		},
		status: {
			default: {
				text: palette.text.secondary,
				background: palette.action.selected,
				outline: darkMode ? grey[700] : grey[400],
				hover: palette.text._states.focus,
				pressed: palette.text._states.focusVisible,
				focus: alpha(palette.text.primary, 0.2)
			},
			error: semanticChip(palette.error, darkMode, lightTheme.palette.error.light),
			warning: semanticChip(palette.warning, darkMode, lightTheme.palette.warning.light),
			info: semanticChip(palette.info, darkMode, lightTheme.palette.info.light),
			success: semanticChip(palette.success, darkMode, lightTheme.palette.success.light)
		}
	};
};
